// TypeScript mirror of the Mai WebSocket contract defined in
// equiphealth/mai `mai_service/src/schemas/ws.ts`. The backend produces
// server -> client frames as plain shapes (not zod-parsed at runtime), so the
// client validates them here with hand-rolled guards. Keep this in sync with
// the backend schema; a drift shows up as a compile error in the frame tests.

// conversationId is omitted: the backend marks it optional and ignores it in
// favor of server-owned rotation.
export interface SendMessageFrame {
  action: 'sendMessage';
  conversationId?: string;
  patientId: string;
  text: string;
  // IANA zone for rendering appointment times as provider-local (MAI-91).
  timezone?: string;
}

export interface CancelFrame {
  action: 'cancel';
  conversationId: string;
}

export type ClientFrame = SendMessageFrame | CancelFrame;

export interface DeltaFrame {
  type: 'delta';
  token: string;
  index: number;
}

export interface DoneFrame {
  type: 'done';
  messageId: string;
}

export interface ErrorFrame {
  type: 'error';
  message: string;
}

export interface PingFrame {
  type: 'ping';
}

export interface ToolCallFrame {
  type: 'tool_call';
  toolUseId: string;
  name: string;
}

export interface ToolResultSource {
  file: string;
  page: number | null;
  url: string;
}

// Server sends 'success' | 'error'; 'pending' is a client-side superset for the
// synthesized tool_call state, tolerated here defensively.
export type ToolResultStatus = 'success' | 'error' | 'pending';

export interface ToolResultFrame {
  type: 'tool_result';
  results: Array<{
    toolUseId: string;
    status: ToolResultStatus;
    sources: ToolResultSource[];
  }>;
}

export type ServerFrame =
  | DeltaFrame
  | DoneFrame
  | ErrorFrame
  | PingFrame
  | ToolCallFrame
  | ToolResultFrame;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSource = (value: unknown): value is ToolResultSource =>
  isRecord(value) &&
  typeof value.file === 'string' &&
  (value.page === null || typeof value.page === 'number') &&
  typeof value.url === 'string';

const asServerFrame = (value: Record<string, unknown>): ServerFrame | null => {
  switch (value.type) {
    case 'delta':
      return typeof value.token === 'string' && typeof value.index === 'number'
        ? { type: 'delta', token: value.token, index: value.index }
        : null;
    case 'done':
      return typeof value.messageId === 'string'
        ? { type: 'done', messageId: value.messageId }
        : null;
    case 'error':
      return typeof value.message === 'string'
        ? { type: 'error', message: value.message }
        : null;
    case 'ping':
      return { type: 'ping' };
    case 'tool_call':
      return typeof value.toolUseId === 'string' &&
        typeof value.name === 'string'
        ? { type: 'tool_call', toolUseId: value.toolUseId, name: value.name }
        : null;
    case 'tool_result':
      return Array.isArray(value.results) &&
        value.results.every(
          (r) =>
            isRecord(r) &&
            typeof r.toolUseId === 'string' &&
            (r.status === 'success' ||
              r.status === 'error' ||
              r.status === 'pending') &&
            Array.isArray(r.sources) &&
            r.sources.every(isSource),
        )
        ? ({ type: 'tool_result', results: value.results } as ToolResultFrame)
        : null;
    default:
      return null;
  }
};

/**
 * Parses a raw WebSocket message into a typed ServerFrame, or null if it is not
 * a recognized frame. Never throws and never logs the raw payload: frame bodies
 * carry PHI (answer tokens, chart queries, file paths), so the null branch is
 * intentionally silent. Callers decide how to react to an unparseable frame.
 */
export const parseServerFrame = (raw: string): ServerFrame | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  return asServerFrame(parsed);
};
